import { z } from 'zod';
import { STOP_SKIP_REASONS } from '../constants/index.js';
import { dateOnlySchema, uuidSchema } from './common.js';
import { customerSchema } from './customer.js';
import { routeSchema } from './route.js';
import { stockBalanceSchema } from './stock.js';
/**
 * The field-staff surface, under `/me`.
 *
 * It is deliberately a separate contract from the admin one rather than the
 * same endpoints with a wider role: every query here is scoped to the signed-in
 * user's own routes, and keeping that in its own module means the scope cannot
 * be forgotten when an admin endpoint grows a new filter.
 */
/** Everything the app needs to open on, for one business day. */
export const fieldDaySchema = z.object({
    /** `YYYY-MM-DD` in Europe/Rome. */
    date: z.string(),
    vehicleId: uuidSchema.nullable(),
    vehiclePlate: z.string().nullable(),
    routes: z.array(routeSchema),
    stopCount: z.number().int(),
    visitedCount: z.number().int(),
    skippedCount: z.number().int(),
    /** Documents this user posted today, and what they collected in cash. */
    documentCount: z.number().int(),
    collectedToday: z.string(),
    salesToday: z.string(),
});
export const skipStopSchema = z
    .object({
    skipReason: z.enum(STOP_SKIP_REASONS),
    skipNote: z.string().trim().max(500).nullish(),
})
    .refine((v) => v.skipReason !== 'other' || Boolean(v.skipNote?.trim()), {
    message: 'routes.errors.skipNoteRequired',
    path: ['skipNote'],
});
/**
 * A customer on one of my routes, with what they owe.
 *
 * The balance travels with the customer because the app caches this list for
 * offline use and the driver needs to know the debt at the door, where there
 * may be no signal to ask for it.
 */
export const fieldCustomerSchema = customerSchema.extend({
    balance: z.string(),
});
/**
 * A product the van is carrying, with what it sells for.
 *
 * Slimmer than `Product` on purpose: this travels to a phone and is cached
 * there, so it holds what a sale screen needs and nothing else. The price is
 * indicative — the server replaces every unit price on posting (D68) — but
 * without it the driver cannot see a total before saving.
 */
export const fieldProductSchema = z.object({
    id: uuidSchema,
    sku: z.string(),
    name: z.string(),
    unit: z.string(),
    price: z.string(),
    vatRate: z.number().int(),
});
/** One download the app can cache and then work from offline. */
export const fieldBundleSchema = z.object({
    date: z.string(),
    day: fieldDaySchema,
    customers: z.array(fieldCustomerSchema),
    vehicleStock: z.array(stockBalanceSchema),
    /** Prices and VAT rates for exactly the products aboard. */
    catalogue: z.array(fieldProductSchema),
    /** Server time when the bundle was built, so the app can show its age. */
    fetchedAt: z.string(),
});
export const fieldDayQuerySchema = z.object({
    /** Defaults to today in Europe/Rome. */
    date: dateOnlySchema.optional(),
});
//# sourceMappingURL=field.js.map